import { Injectable, inject, OnDestroy } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/**
 * Custom internationalization service for MatPaginator
 * Provides translated labels for paginator controls
 */
@Injectable({
  providedIn: 'root',
})
export class PaginatorIntlService extends MatPaginatorIntl implements OnDestroy {
  private translateService = inject(TranslateService);
  private subscription?: Subscription;

  constructor() {
    super();
    this.subscription = this.translateService.onLangChange.subscribe(() => {
      this.getAndInitTranslations();
    });
    this.getAndInitTranslations();
  }

  /**
   * Cleans up subscriptions when the service is destroyed
   */
  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  /**
   * Initializes translations for paginator labels
   */
  getAndInitTranslations(): void {
    this.itemsPerPageLabel = this.translateService.instant('paginator.itemsPerPage');
    this.nextPageLabel = this.translateService.instant('paginator.nextPage');
    this.previousPageLabel = this.translateService.instant('paginator.previousPage');
    this.firstPageLabel = this.translateService.instant('paginator.firstPage');
    this.lastPageLabel = this.translateService.instant('paginator.lastPage');
    this.changes.next();
  }

  /**
   * Customizes the range label with translations
   * @param page Current page index
   * @param pageSize Number of items per page
   * @param length Total number of items
   * @returns Formatted range label
   */
  override getRangeLabel = (page: number, pageSize: number, length: number): string => {
    if (length === 0 || pageSize === 0) {
      return this.translateService.instant('paginator.rangeZero', { length });
    }
    const start = page * pageSize + 1;
    const end = Math.min((page + 1) * pageSize, length);
    return this.translateService.instant('paginator.range', { start, end, length });
  };
}
